import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ViewChildren,
  QueryList,
} from "@angular/core";
import { Subscription, Observable, EMPTY } from "rxjs";
import { catchError, map } from "rxjs/operators";
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from "@angular/common/http";
import { isEmptyString, isEmptyValue } from "@helpers";
import { ApplicationUtilsService, DocumentLoaderService, EcertMeldingService } from "@util-services";
import { EcertInputComponent } from "@building-blocks/ecertnl-input/ecertnl-input.component";
import { EphytoCertificaatZoekparameters, ResourceApiService } from "@api-services/resource-api.service";
import { Certificaat, ErrorMessage, Gebied, Resultaten} from "@models";
import { EcertFormatPipe } from "@pipes/ecertnl-format.pipe";
import { DatePipe } from '@angular/common';
import { ContextRoot } from '@util-services/http-wrapper/http-wrapper.service';

@Component({
  selector: "app-check-certificate",
  templateUrl: "./check-certificate.component.html",
  styleUrls: ["./check-certificate.component.css"],
  providers: [EcertFormatPipe],
  standalone: false,
})
export class CheckCertificateComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  constructor(
    private httpClient: HttpClient,
    private documentUtils: DocumentLoaderService,
    private appUtils: ApplicationUtilsService,
    private datePipe: DatePipe,
    protected meldingenService: EcertMeldingService,
    protected resourceApiService: ResourceApiService,
  ) {
  }

  // Excluded array contains the characters which are excluded from the captcha-string.
  excludedArray = ["0", "1", "o", "l", "i", "O", "L", "I"];

  private subscriptions: Subscription[] = [];
  protected zoekparameters: EphytoCertificaatZoekparameters;
  protected captcha = { captchaInput: "" };
  protected landen: Gebied[] = [];
  protected spinnerxml = false;
  protected spinnerpdf = false;
  private resultaten: Resultaten;
  protected resultatenTekst = "";
  protected isXmlOpgehaald = false;
  protected isPdfOpgehaald = false;
  protected isResultatenOphalenBezig = false;
  private errorTekst: string = "";
  private certificaat: Certificaat;
  protected landVanAfgifteError = false;
  protected captchaError = false;
  private canvas: HTMLCanvasElement;
  private captchaText: string;
  private captchaTextGenerator: () => string = () => "";
  private errorMessage: ErrorMessage;

  @ViewChildren(EcertInputComponent)
  inputs: QueryList<EcertInputComponent>;

  ngOnInit() {
    this.getInitReferences();
    this.getOptions();
    this.zoekparameters = {
      certificaatNummer: this.appUtils.getCertificaatNummer(),
      landVanAfgifte: this.appUtils.getLandVanAfgifte(),
      datumVanAfgifte: new Date()
    };
    this.captcha.captchaInput = this.appUtils.getCaptchaInput()
    this.errorMessage = this.appUtils.getErrorMessage();
    this.subscriptions.push(this.resourceApiService.getLanden().subscribe((res: Gebied[]) => (this.landen = res)));
  }

  ngAfterViewInit() {
    this.getCaptchaText(false);
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.resultaten = null;
  }

  getContextRoot(): string {
    return this.appUtils.getContextRoot();
  }

  getOptions() {
    const headers = new HttpHeaders({
      "Content-Type": "application/json; charset=utf-8",
    });
    return { headers: headers };
  }

  hasErrors() {
    return this.inputs ? this.inputs.some(input => input.hasErrors()) : false;
  }

  verifyInputValues() {

    if (this.captcha.captchaInput === this.captchaText) {
      // Bij een correcte captcha wordt het certificaat gecontroleerd
      // en wordt een reload gedaan van de captcha zonder captcha-foutmelding.
      this.checkCertificate();
      this.reloadCaptchaText(false);
    } else {
      // Bij een incorrecte captcha wordt het certificaat niet gecontroleerd
      // en wordt een reload gedaan van de captcha met een captcha-foutmelding.
      this.setResultatenOpgehaald(false);
      this.reloadCaptchaText(true);
    }
  }

  setCaptchaError(error: boolean) {
    const captchaInput = this.inputs?.find(input => input.id === 'captcha_input');
    if (captchaInput) {
      captchaInput.error = error;
    }
  }

  openXmlCertificaat(): void {
    this.spinnerxml = true;
    this.meldingenService.voegMeldingToe(null);
    this.documentUtils.openFile(this.resultaten.certList[0].xml_cert, "certificate", "application/xml");
    this.spinnerxml = false;
  }

  openPdfCertificaat(): void {
    this.spinnerpdf = true;
    this.meldingenService.voegMeldingToe(null);
    this.documentUtils.openFileFromB64(this.resultaten.certList[0].pdf_cert, "certificate", "application/pdf");
    this.spinnerpdf = false;
  }

checkCertificate() {
  this.isResultatenOphalenBezig = true;
  this.landVanAfgifteError = false;

  if (this.hasErrors()) {
    return;
  }
  this.setResultaten(null);
  this.setResultatenOpgehaald(false);

  // Kopieer zoekparameters naar certificaat
  this.certificaat = JSON.parse(JSON.stringify(this.zoekparameters));

  // Opslaan van input-values in de service
  this.appUtils.setCertificaatNummer(this.certificaat.certificaatNummer);
  this.appUtils.setLandVanAfgifte(this.certificaat.landVanAfgifte);
  this.appUtils.setDatumVanAfgifte(this.certificaat.datumVanAfgifte);
  this.appUtils.setCaptchaInput(this.captcha.captchaInput);

  let resError = null;
  this.setResultaatError(null);
  this.errorTekst = null;
  this.subscriptions.push(
    this.httpClient
      .post(
        "/" + this.getContextRoot() + "/certificateRequest",
        JSON.stringify(this.certificaat),
        this.getOptions()
      )
      .pipe(
        catchError((err: HttpErrorResponse, caught: Observable<any>) => {
          if (err.status === 0) {
            let updatedError = new HttpErrorResponse({
              ...err,
              statusText: this.errorMessage.foutmeldingOverig,
            });
            this.errorTekst = `${this.errorMessage.foutmelding} ${updatedError.status} (${updatedError.statusText})`;
          } else {
            this.errorTekst = `${this.errorMessage.foutmelding} ${err.status} (${err.statusText})`;
          }
          resError = {
            header: this.errorMessage.headerFoutmelding,
            tekst: this.errorTekst,
          };
          this.setResultaatError(resError);
          this.reloadCaptchaText(false);
          this.isResultatenOphalenBezig = false;
          return EMPTY;
        }),
        map((res: any) => {
          this.resultaten = res;
          if (this.resultaten != null) {
            if (this.resultaten.status === "OK") {
              this.setResultaten(this.resultaten.certList);
              this.setResultatenOpgehaald(true);
              this.isResultatenOphalenBezig = false;
              this.isXmlOpgehaald = !isEmptyValue(this.resultaten.certList[0].xml_cert);
              this.isPdfOpgehaald = !isEmptyValue(this.resultaten.certList[0].pdf_cert);
              const landnaam = this.landen.find(l => l.code === this.certificaat.landVanAfgifte)?.naam;
              const datumVanAfgifte = this.datePipe.transform(this.certificaat.datumVanAfgifte, 'dd-MM-yyyy');
              this.resultatenTekst = "Certificate " + this.certificaat.certificaatNummer +
                ", " + landnaam + " (" + datumVanAfgifte + ")";
            } else {
              const header = "";
              const foutmelding = this.getErrorTekst(this.resultaten.foutmelding);
              let tekst;
              if (!this.isEmptyValue(foutmelding)) {
                tekst = foutmelding;
              } else {
                tekst = this.getPrompt("DOWNLOAD_EPHYTO.GEEN_CERTIFICAAT_GEVONDEN");
              }
              resError = {
                header: header,
                tekst: tekst,
              };
              this.setResultaatError(resError);
              this.reloadCaptchaText(false);
              this.isResultatenOphalenBezig = false;
            }
          } else {
            this.isResultatenOphalenBezig = false;
          }
        })
      )
      .subscribe()
    );
  }

  getErrorTekst(foutmelding) {
    return foutmelding;
  }

  findIsDisabled() {
    return (
      this.hasErrors() ||
      isEmptyValue(this.zoekparameters) ||
      isEmptyString(this.zoekparameters.certificaatNummer) ||
      isEmptyString(this.zoekparameters.landVanAfgifte) ||
      isEmptyValue(this.zoekparameters.datumVanAfgifte) ||
      isEmptyString(this.captcha.captchaInput)
    );
  }

  getPrompt(prompt) {
    return this.appUtils.getPrompt(prompt);
  }

  getResultaten() {
    return this.appUtils.getResultaten();
  }
  setResultaten(resultaten) {
    this.appUtils.setResultaten(resultaten);
  }

  isResultatenOpgehaald() {
    return this.appUtils.isResultatenOpgehaald();
  }

  setResultatenOpgehaald(resultatenOpgehaald: boolean) {
    this.appUtils.setResultatenOpgehaald(resultatenOpgehaald);
  }

  getResultaatError() {
    return this.appUtils.getResultaatError();
  }

  setResultaatError(resultaatError: any) {
    this.appUtils.setResultaatError(resultaatError);
  }

  getInitReferences() {
    this.canvas = document.getElementById("canvas") as HTMLCanvasElement;
    this.captchaText = "";
    this.captchaTextGenerator = () => {
      let generatedText = "";

      // uppercaseArray contains uppercase characters (ascii code 65-90), excluding characters from excludeArray
      const uppercaseArray = this.getArray(65, 90);

      // lowercaseArray contains lowercase characters (ascii code 97-122), excluding characters from excludeArray
      const lowercaseArray = this.getArray(97, 122);

      // numberArray contains the characters from 0-9 (ascii code 48-57), excluding characters from excludeArray
      const numberArray = this.getArray(48, 57);

      // Total 9 characters: 3 capital, 3 small and 3 numbers
      for (let i = 0; i < 3; i++) {
        let randomNumber;
        randomNumber = this.getRandomNumber(0, uppercaseArray.length - 1);
        generatedText += uppercaseArray[randomNumber];
        randomNumber = this.getRandomNumber(0, lowercaseArray.length - 1);
        generatedText += lowercaseArray[randomNumber];
        randomNumber = this.getRandomNumber(0, numberArray.length - 1);
        generatedText += numberArray[randomNumber];
      }
      return generatedText;
    };
  }

  getArray(min: number, max: number): string[] {
    // returns an array based on ascii numbers, excluding characters from excludeArray
    const arr: string[] = [];

    for (let i = min; i <= max; i++) {
      const char = String.fromCharCode(i);

      if (!this.excludedArray.includes(char)) {
        arr.push(char);
      }
    }

    return arr;
  }

  // Bepaal random nummer op een cryptografisch veilige manier
  getRandomNumber(min: number, max: number): number {

    // Bereken hoeveel mogelijke waarden er zijn.
    // Bijvoorbeeld: min=1 en max=10 => bereik = 10 (1,2,3,4,5,6,7,8,9,10)
    const range = max - min + 1;

    // Controleer of het bereik geldig is.
    // Als min groter is dan max ontstaat een ongeldig bereik.
    if (range <= 0) {
      throw new Error("Invalid range");
    }

    // Grootste mogelijke waarde van een unsigned 32-bit integer.
    // Hexadecimaal: FFFFFFFF = decimaal: 4294967295
    const maxUint32 = 0xFFFFFFFF;

    // Bereken de hoogste waarde die we mogen gebruiken zonder modulo bias.
    // Hierdoor krijgen alle uitkomsten exact dezelfde kans.
    const limit = maxUint32 - (maxUint32 % range);

    // Maak een array van 1 unsigned 32-bit integer.
    // crypto.getRandomValues() vult deze met een cryptografisch veilig willekeurig getal.
    const values = new Uint32Array(1);

    // Variabele waarin het willekeurige getal wordt opgeslagen.
    let randomValue: number;

    do {
      // Vraag een cryptografisch veilig willekeurig getal op.
      // Het resultaat komt terecht in values[0].
      crypto.getRandomValues(values);

      // Haal de gegenereerde waarde uit de array.
      randomValue = values[0];

      // Als de waarde groter of gelijk is aan de limiet, genereren we opnieuw een getal.
      // Dit voorkomt modulo bias.
    } while (randomValue >= limit);

    // Zet het grote willekeurige getal om naar een waarde binnen het gewenste bereik.
    // randomValue % range geeft een waarde tussen 0 en range-1
    // Door min erbij op te tellen krijgen we min t/m max
    return min + (randomValue % range);
  }

  drawStringOnCanvas(captchaText) {
    const ctx = this.canvas.getContext("2d");
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    const textColors = ["rgb(0,0,0)", "rgb(130,130,130)"];
    const letterSpace = 180 / captchaText.length;

    for (let i = 0; i < captchaText.length; i++) {
      const xInitialSpace = 25;
      ctx.font = "22px Roboto Mono";
      ctx.fillStyle = textColors[this.getRandomNumber(0, textColors.length - 1)];
      ctx.fillText(
        captchaText[i],
        xInitialSpace + i * letterSpace,
        this.getRandomNumber(25, 40),
        100
      );
    }
  }

  reloadCaptchaText(error: boolean) {
    this.captcha.captchaInput = "";
    this.appUtils.setCaptchaInput("");
    this.getCaptchaText(error);
  }

  getCaptchaText(error: boolean) {

    // Foutstatus instellen
    this.setCaptchaError(error);

    // Nieuwe captcha genereren
    this.captchaText = this.captchaTextGenerator();

    // String omzetten naar een array van losse karakters
    const characters = this.captchaText.split("");

    // Fisher-Yates shuffle
    for (let i = characters.length - 1; i > 0; i--) {

      // Kies een willekeurige index tussen 0 en i
      const j = this.getRandomNumber(0, i);

      // Verwissel de twee karakters
      [characters[i], characters[j]] = [characters[j], characters[i]];
    }

    // Array weer omzetten naar een string
    this.captchaText = characters.join("");

    // Captcha tekenen
    this.drawStringOnCanvas(this.captchaText);
  }

  protected readonly isEmptyValue = isEmptyValue;
}
